import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EmergencyGuide = ({ onEmergencyClick }) => {
  const [activeStep, setActiveStep] = useState(null);

  const emergencySteps = [
    {
      id: 1,
      icon: "AlertTriangle",
      title: "Locate Main Water Valve",
      description: "Find your main water shut-off valve - usually near the water meter or where the main line enters your home.",
      details: "Turn the valve clockwise until it stops. This will stop water flow to your entire property. If you have a gate valve, turn it several times. For ball valves, turn 90 degrees.",
      urgency: "critical"
    },
    {
      id: 2,
      icon: "Power",
      title: "Turn Off Water Heater",
      description: "Prevent damage to your water heater by turning off the power supply or gas valve.",
      details: "For electric heaters, switch off the circuit breaker. For gas heaters, turn the gas valve to the 'pilot' position. Never leave a water heater running when water supply is shut off.",
      urgency: "high"
    },
    {
      id: 3,
      icon: "Droplets",
      title: "Open Faucets to Drain",
      description: "Open all faucets to drain remaining water from pipes and relieve pressure.",
      details: "Start with the highest faucet in your home and work your way down. This prevents water hammer and reduces pressure in the system. Leave faucets open until help arrives.",
      urgency: "medium"
    },
    {
      id: 4,
      icon: "Phone",
      title: "Call Professional Help",
      description: "Contact Gert immediately for expert emergency plumbing service.",
      details: "Our 24/7 emergency line: 082 793 5785. Provide details about the leak location, severity, and any steps you've already taken. We'll dispatch help immediately.",
      urgency: "critical"
    }
  ];

  const whenToCallImmediately = [
    {
      icon: "Flame",
      title: "Burst Pipes",
      description: "Water gushing from pipes or walls requires immediate professional attention to prevent structural damage."
    },
    {
      icon: "Home",
      title: "Flooding",
      description: "Standing water in your home can cause extensive damage. Call immediately to minimize property loss."
    },
    {
      icon: "Zap",
      title: "Gas Smell",
      description: "Any gas odor near plumbing requires emergency response. Evacuate and call from outside."
    },
    {
      icon: "Droplet",
      title: "No Water Supply",
      description: "Complete loss of water supply may indicate a serious main line issue requiring expert diagnosis."
    },
    {
      icon: "AlertCircle",
      title: "Sewage Backup",
      description: "Sewage backing up into your home is a health hazard requiring immediate professional intervention."
    },
    {
      icon: "Thermometer",
      title: "Water Heater Failure",
      description: "Leaking or malfunctioning water heaters can cause flooding and require urgent professional service."
    }
  ];

  const urgencyColors = {
    critical: "bg-error/10 border-error text-error",
    high: "bg-warning/10 border-warning text-warning",
    medium: "bg-accent/10 border-accent text-accent"
  };

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 bg-error/10 px-4 py-2 rounded-full mb-4">
            <Icon name="AlertTriangle" size={20} color="var(--color-error)" />
            <span className="text-sm font-semibold text-error">Emergency Response Guide</span>
          </div>
          <h2 className="font-headline text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            What to Do in a Plumbing Emergency
          </h2>
          <p className="font-body text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
            Follow these critical steps to minimize damage while help is on the way. Every second counts in a plumbing emergency.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-12 md:mb-16">
          {emergencySteps?.map((step) => (
            <div 
              key={step?.id}
              className={`bg-card border-2 rounded-xl p-6 cursor-pointer transition-all duration-300 ${
                activeStep === step?.id 
                  ? `${urgencyColors?.[step?.urgency]} shadow-lg scale-105` 
                  : 'border-border hover:border-primary hover:shadow-md'
              }`}
              onClick={() => setActiveStep(activeStep === step?.id ? null : step?.id)}
            >
              <div className="flex items-start gap-4 mb-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                  activeStep === step?.id 
                    ? urgencyColors?.[step?.urgency]
                    : 'bg-primary/10'
                }`}>
                  <Icon 
                    name={step?.icon} 
                    size={24} 
                    color={activeStep === step?.id ? 'currentColor' : 'var(--color-primary)'}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-accent text-sm font-semibold mb-1">Step {step?.id}</div>
                  <h3 className="font-headline text-lg font-semibold mb-2">{step?.title}</h3>
                </div>
              </div>
              
              <p className="text-sm text-muted-foreground mb-3">{step?.description}</p>
              
              {activeStep === step?.id && (
                <div className="mt-4 pt-4 border-t border-border">
                  <p className="text-sm leading-relaxed">{step?.details}</p>
                </div>
              )}
              
              <div className="flex items-center gap-2 mt-4">
                <Icon name={activeStep === step?.id ? "ChevronUp" : "ChevronDown"} size={16} />
                <span className="text-xs font-medium">
                  {activeStep === step?.id ? "Show less" : "Show details"}
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-br from-error/5 to-warning/5 rounded-2xl p-8 md:p-12 border border-error/20">
          <div className="text-center mb-8">
            <h3 className="font-headline text-2xl md:text-3xl font-bold text-foreground mb-4">
              When to Call Immediately
            </h3>
            <p className="font-body text-muted-foreground max-w-2xl mx-auto">
              These situations require immediate professional intervention. Don't wait - call our emergency line now.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-8">
            {whenToCallImmediately?.map((situation, index) => (
              <div 
                key={index}
                className="bg-card rounded-xl p-6 border border-border hover:border-error hover:shadow-md transition-all duration-300"
              >
                <div className="w-12 h-12 bg-error/10 rounded-full flex items-center justify-center mb-4">
                  <Icon name={situation?.icon} size={24} color="var(--color-error)" />
                </div>
                <h4 className="font-headline text-lg font-semibold mb-2">{situation?.title}</h4>
                <p className="text-sm text-muted-foreground">{situation?.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Button 
              variant="default"
              size="xl"
              className="bg-error hover:bg-error/90 text-error-foreground emergency-cta"
              iconName="Phone"
              iconPosition="left"
              onClick={onEmergencyClick}
            >
              Emergency Line: 082 793 5785
            </Button>
            <p className="text-sm text-muted-foreground mt-4">
              Available 24/7 • Average response time: Under 60 minutes
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EmergencyGuide;